import c3, { ChartConfiguration } from 'c3'
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

type ExampleItem = { title: string; value: number; color: string }

// eslint-disable-next-line no-use-before-define
@Component<C3ChartMixin>({
  watch: {
    c3data: {
      handler() {
        this.reloadC3Chart()
      },
      deep: true,
    },
    switchingSecondaryApps() {
      this.reloadC3Chart()
    },
  },
})
export default class C3ChartMixin extends Vue.extend({
  props: {
    chartId: {
      type: String,
      default: null,
    },
    items: {
      type: Array as PropType<ExampleItem[]>,
      default: () => [
        { title: 'Line1', value: 100, color: 'red' },
        { title: 'Line2', value: 50, color: 'blue' },
        { title: 'Line3', value: 25, color: 'green' },
      ],
    },
    json: {
      type: Array as Extract<c3.Data, 'json'>,
      default: null,
    },
    keys: {
      type: Object,
      default: null,
    },
    type: {
      type: String as PropType<c3.ChartType>,
      default: null,
      validator: (value: c3.ChartType) => {
        // The value must match one of these strings
        return (
          [
            'line',
            'spline',
            'step',
            'area',
            'area-spline',
            'area-step',
            'bar',
            'scatter',
            'stanford',
            'pie',
            'donut',
            'gauge',
          ].indexOf(value) !== -1
        )
      },
    },
    names: {
      type: Object,
      default: null,
    },
    labels: {
      type: Object,
      default: null,
    },
    colors: {
      type: Object,
      default: null,
    },
    color: {
      type: Function as PropType<Extract<c3.Data, 'color'>>,
      default: null,
    },
    axis: {
      type: Object,
      default: null,
    },
    legend: {
      type: Object,
      default: null,
    },
    tooltip: {
      type: Object,
      default: null,
    },
    formatValue: {
      type: Function,
      default: null,
    },
  },
}) {
  id = 'chart'
  chart: c3.ChartAPI | null = null

  get c3json() {
    return this.items.map(({ title, value }: { title: string; value: number }) => ({
      title,
      [title]: value,
    }))
  }

  get c3keys() {
    const headings = this.items.map(({ title }) => title)
    return {
      x: 'title',
      value: headings,
    }
  }

  get c3names() {
    return this.names || {}
  }

  get c3type() {
    return this.type
  }

  get c3labels() {
    return this.labels
  }

  get c3colors() {
    return this.items.reduce((output: { [key: string]: string }, { title, color }) => {
      output[title] = color
      return output
    }, {})
  }

  get c3color() {
    return this.color
  }

  get c3data() {
    return {
      json: this.json || this.c3json,
      keys: this.keys || this.c3keys,
      names: this.names || this.c3names,
      type: this.type || this.c3type,
      labels: this.labels || this.c3labels,
      colors: this.colors || this.c3colors,
      color: this.color || this.c3color,
    }
  }

  get c3axis() {
    return this.axis
  }

  get c3legend() {
    return this.legend
  }

  get c3tooltip() {
    return (
      this.tooltip || {
        format: {
          value: this.formatValue,
        },
      }
    )
  }

  get c3config() {
    return {
      data: this.c3data,
      axis: this.c3axis,
      legend: this.c3legend,
      tooltip: this.c3tooltip,
    }
  }

  get switchingSecondaryApps() {
    return this.$typedStore.public.display.switchingSecondaryApps
  }

  mounted() {
    this.chart = this.generateChart()
  }

  beforeDestroy() {
    if (this.chart) this.chart.destroy()
  }

  reloadC3Chart() {
    // Do not reload the data when we are switching between secondary apps
    // because otherwise the reference to the data gets removed when the new
    // secondary app is loaded causing a c3 error
    if (this.chart && !this.switchingSecondaryApps) {
      this.chart.load({ ...this.c3data, unload: true })
    }
  }

  generateChart() {
    return c3.generate(this.c3config as ChartConfiguration)
  }
}
